// src/component/PromoBannerAdmin.js
//
// 「サイト上部プッシュ通知バナー／ダイアログ」の管理画面。
// systemConfig/promoBanner を編集する。

import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Button,
  Stack,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
} from "@mui/material";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../api/firebase";
import { AppThemeContext } from "./AppShell";
import {
  PROMO_BANNER_CONFIG_DOC,
  PROMO_BANNER_DEFAULTS,
  PROMO_BANNER_AUDIENCE,
  PROMO_BANNER_PHASE_OPTIONS,
} from "./promoBannerDefaults";
import PromoBannerDialog from "./PromoBannerDialog";
import { ensureServerTimeSynced, serverNowMs } from "../api/serverTime";

function parseExpiresAt(expiresAt) {
  if (!expiresAt || typeof expiresAt !== "string") return null;
  const m = expiresAt.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!m) return null;
  return new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    m[6] ? Number(m[6]) : 0,
    0
  );
}

function formatExpiresPreview(expiresAt) {
  const date = parseExpiresAt(expiresAt);
  if (!date) return null;
  const weekDayJa = ["日", "月", "火", "水", "木", "金", "土"][date.getDay()];
  const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
  const label = `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${weekDayJa}）${pad2(
    date.getHours()
  )}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())} まで表示`;
  const isPast = serverNowMs() > date.getTime();
  return { label, isPast };
}

export default function PromoBannerAdmin() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFFFFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";

  const [enabled, setEnabled] = useState(PROMO_BANNER_DEFAULTS.enabled);
  const [adminBypassLimits, setAdminBypassLimits] = useState(
    PROMO_BANNER_DEFAULTS.adminBypassLimits
  );
  const [audienceMode, setAudienceMode] = useState(
    PROMO_BANNER_DEFAULTS.audience.mode
  );
  const [audiencePhases, setAudiencePhases] = useState(
    PROMO_BANNER_DEFAULTS.audience.phases
  );
  const [tooltipText, setTooltipText] = useState(
    PROMO_BANNER_DEFAULTS.tooltipText
  );
  const [chipLabel, setChipLabel] = useState(PROMO_BANNER_DEFAULTS.chipLabel);
  const [title, setTitle] = useState(PROMO_BANNER_DEFAULTS.title);
  const [body, setBody] = useState(PROMO_BANNER_DEFAULTS.body);
  const [linkUrl, setLinkUrl] = useState(PROMO_BANNER_DEFAULTS.linkUrl);
  const [linkLabel, setLinkLabel] = useState(PROMO_BANNER_DEFAULTS.linkLabel);
  const [expiresAt, setExpiresAt] = useState(PROMO_BANNER_DEFAULTS.expiresAt);

  const [imageUrl, setImageUrl] = useState(PROMO_BANNER_DEFAULTS.imageUrl);
  const [imagePath, setImagePath] = useState(PROMO_BANNER_DEFAULTS.imagePath);

  const [version, setVersion] = useState(PROMO_BANNER_DEFAULTS.version);

  const [loading, setLoading] = useState(true);
  const [savingText, setSavingText] = useState(false);
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [savingAudience, setSavingAudience] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    ensureServerTimeSynced();
  }, []);

  // 設定購読
  useEffect(() => {
    const ref = doc(db, "systemConfig", PROMO_BANNER_CONFIG_DOC);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() || {} : {};
        setEnabled(data.enabled === true);
        // 既定値は true（管理者は制限なし）
        setAdminBypassLimits(data.adminBypassLimits !== false);
        setAudienceMode(
          data.audience?.mode ?? PROMO_BANNER_DEFAULTS.audience.mode
        );
        setAudiencePhases(
          Array.isArray(data.audience?.phases)
            ? data.audience.phases.map(String)
            : PROMO_BANNER_DEFAULTS.audience.phases
        );
        setTooltipText(
          data.tooltipText ?? PROMO_BANNER_DEFAULTS.tooltipText
        );
        setChipLabel(data.chipLabel ?? PROMO_BANNER_DEFAULTS.chipLabel);
        setTitle(data.title ?? PROMO_BANNER_DEFAULTS.title);
        setBody(data.body ?? PROMO_BANNER_DEFAULTS.body);
        setLinkUrl(data.linkUrl ?? PROMO_BANNER_DEFAULTS.linkUrl);
        setLinkLabel(data.linkLabel ?? PROMO_BANNER_DEFAULTS.linkLabel);
        setExpiresAt(data.expiresAt ?? PROMO_BANNER_DEFAULTS.expiresAt);
        setImageUrl(data.imageUrl ?? PROMO_BANNER_DEFAULTS.imageUrl);
        setImagePath(data.imagePath ?? PROMO_BANNER_DEFAULTS.imagePath);
        setVersion(Number(data.version ?? PROMO_BANNER_DEFAULTS.version));
        setLoading(false);
      },
      (err) => {
        console.error("PromoBannerAdmin onSnapshot error:", err);
        setLoading(false);
        setSnackbar({
          open: true,
          message: "設定の取得に失敗しました。",
          type: "error",
        });
      }
    );
    return () => unsub();
  }, []);

  const showSnack = useCallback((message, type = "success") => {
    setSnackbar({ open: true, message, type });
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const persist = async (patch, options = {}) => {
    const ref = doc(db, "systemConfig", PROMO_BANNER_CONFIG_DOC);
    const data = { ...patch, updatedAt: serverTimestamp() };
    if (options.bumpVersion) {
      // version をインクリメント → 以前「もう表示しない」したユーザーにも再表示される
      data.version = increment(1);
    }
    await setDoc(ref, data, { merge: true });
  };

  const handleToggle = async (event) => {
    const value = event.target.checked;
    setEnabled(value);
    try {
      await persist({ enabled: value });
      showSnack(
        value
          ? "プッシュ通知バナーを「表示する」に変更しました。"
          : "プッシュ通知バナーを「表示しない」に変更しました。"
      );
    } catch (err) {
      console.error("PromoBannerAdmin toggle error:", err);
      showSnack("更新に失敗しました。", "error");
    }
  };

  const handleToggleAdminBypass = async (event) => {
    const value = event.target.checked;
    setAdminBypassLimits(value);
    try {
      await persist({ adminBypassLimits: value });
      showSnack(
        value
          ? "管理者の表示制限を「無効」にしました（テスト用）。"
          : "管理者の表示制限を「有効」にしました。"
      );
    } catch (err) {
      console.error("PromoBannerAdmin adminBypass toggle error:", err);
      showSnack("更新に失敗しました。", "error");
    }
  };

  const handleSaveAudience = async () => {
    setSavingAudience(true);
    try {
      const phases =
        audienceMode === PROMO_BANNER_AUDIENCE.PHASES
          ? Array.from(new Set(audiencePhases.map(String))).sort()
          : [];
      await persist(
        { audience: { mode: audienceMode, phases } },
        { bumpVersion: true }
      );
      showSnack("表示対象を保存しました。");
    } catch (err) {
      console.error("PromoBannerAdmin save audience error:", err);
      showSnack("表示対象の保存に失敗しました。", "error");
    } finally {
      setSavingAudience(false);
    }
  };

  const togglePhase = (id) => {
    setAudiencePhases((prev) => {
      const has = prev.includes(id);
      if (has) return prev.filter((p) => p !== id);
      return [...prev, id];
    });
  };

  const handleSaveText = async () => {
    setSavingText(true);
    try {
      await persist(
        { tooltipText, chipLabel, title, body, linkUrl, linkLabel },
        { bumpVersion: true }
      );
      showSnack(
        "文言・リンクを保存しました。以前「もう表示しない」を選んだユーザーにも再表示されます。"
      );
    } catch (err) {
      console.error("PromoBannerAdmin save text error:", err);
      showSnack("保存に失敗しました。", "error");
    } finally {
      setSavingText(false);
    }
  };

  const handleResetText = () => {
    setTooltipText(PROMO_BANNER_DEFAULTS.tooltipText);
    setChipLabel(PROMO_BANNER_DEFAULTS.chipLabel);
    setTitle(PROMO_BANNER_DEFAULTS.title);
    setBody(PROMO_BANNER_DEFAULTS.body);
    setLinkUrl(PROMO_BANNER_DEFAULTS.linkUrl);
    setLinkLabel(PROMO_BANNER_DEFAULTS.linkLabel);
  };

  const handleSaveExpiry = async () => {
    setSavingExpiry(true);
    try {
      await persist({ expiresAt: expiresAt || "" });
      showSnack(
        expiresAt
          ? `表示終了日時を ${expiresAt} に設定しました。`
          : "表示終了日時をクリアしました（期限なし）。"
      );
    } catch (err) {
      console.error("PromoBannerAdmin save expiry error:", err);
      showSnack("期日の保存に失敗しました。", "error");
    } finally {
      setSavingExpiry(false);
    }
  };

  const handleClearExpiry = () => setExpiresAt("");

  const handleImagePick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const path = `promoBanner/${Date.now()}-${file.name}`;
      const ref = storageRef(storage, path);
      const snap = await uploadBytes(ref, file);
      const url = await getDownloadURL(snap.ref);

      // 旧画像を削除
      if (imagePath) {
        try {
          await deleteObject(storageRef(storage, imagePath));
        } catch (delErr) {
          console.warn("Failed to delete old promo banner image:", delErr);
        }
      }

      await persist(
        { imageUrl: url, imagePath: snap.ref.fullPath },
        { bumpVersion: true }
      );
      showSnack("画像をアップロードしました。");
    } catch (err) {
      console.error("PromoBannerAdmin image upload error:", err);
      showSnack("画像のアップロードに失敗しました。", "error");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = async () => {
    if (!imageUrl && !imagePath) return;
    setUploadingImage(true);
    try {
      if (imagePath) {
        try {
          await deleteObject(storageRef(storage, imagePath));
        } catch (delErr) {
          console.warn("Failed to delete promo banner image:", delErr);
        }
      }
      await persist({ imageUrl: "", imagePath: "" }, { bumpVersion: true });
      showSnack("画像を削除しました。");
    } catch (err) {
      console.error("PromoBannerAdmin image remove error:", err);
      showSnack("画像の削除に失敗しました。", "error");
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const expiryInfo = formatExpiresPreview(expiresAt);

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.18em",
            fontSize: 11,
            opacity: 0.8,
            color: textBase,
          }}
        >
          ADMIN
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase }}
        >
          サイト上部プッシュ通知 管理
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}>
          ログイン中の全ユーザーに対して、サイト上部に常時表示する「プッシュ通知バナー」と、ログインの度に1日1回表示される「ダイアログ」の文言・画像・リンク・表示終了日時を管理します。
        </Typography>
        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)" }}>
          現在のバージョン：v{version}（文言／画像を保存するとバージョンが上がり、過去に「今後表示しない」したユーザーにも再表示されます）
        </Typography>
      </Box>

      {/* ON/OFF カード */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              background:
                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
              boxShadow:
                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <CampaignRoundedIcon sx={{ fontSize: 30, color: accent }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
            >
              バナー／ダイアログの表示
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
              「表示する」にすると、サイト上部にバナーが常時表示され、ログイン後の最初のアクセスで1日1回ダイアログが開きます。
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Switch checked={enabled} onChange={handleToggle} color="primary" />
          }
          label={
            <Typography sx={{ fontWeight: 700, color: textBase }}>
              {enabled ? "現在：表示する（有効）" : "現在：表示しない（停止中）"}
            </Typography>
          }
        />

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Switch
              checked={adminBypassLimits}
              onChange={handleToggleAdminBypass}
              color="primary"
            />
          }
          label={
            <Typography sx={{ fontWeight: 700, color: textBase }}>
              {adminBypassLimits
                ? "管理者は制限なし（毎回開く・テスト用）"
                : "管理者にも他ユーザーと同じ制限を適用"}
            </Typography>
          }
        />
        <Typography
          variant="body2"
          sx={{ mt: 0.5, ml: 5, color: "rgba(0,0,0,0.65)" }}
        >
          ONにすると、管理者（isAdmin === true）は「1日1回」「今後表示しない」の制限を受けず、リロードのたびにダイアログが開きます。テストや文言確認に便利です。
        </Typography>
      </Paper>

      {/* 表示対象（公開範囲） */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              background:
                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
              boxShadow:
                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <VisibilityRoundedIcon sx={{ fontSize: 30, color: accent }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
            >
              表示対象
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
              バナー／ダイアログを誰に見せるかを切り替えます。管理者には常に表示されます（プレビュー・運用のため）。
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <RadioGroup
          value={audienceMode}
          onChange={(e) => setAudienceMode(e.target.value)}
        >
          <FormControlLabel
            value={PROMO_BANNER_AUDIENCE.ADMIN}
            control={<Radio />}
            label={
              <Typography sx={{ fontWeight: 700, color: textBase }}>
                管理者のみ（テスト中）
              </Typography>
            }
          />
          <FormControlLabel
            value={PROMO_BANNER_AUDIENCE.PHASES}
            control={<Radio />}
            label={
              <Typography sx={{ fontWeight: 700, color: textBase }}>
                指定した期のユーザーのみ（複数選択可）
              </Typography>
            }
          />

          {audienceMode === PROMO_BANNER_AUDIENCE.PHASES && (
            <Box sx={{ pl: 4, mt: 0.5, mb: 0.5 }}>
              <FormGroup row>
                {PROMO_BANNER_PHASE_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.id}
                    control={
                      <Checkbox
                        checked={audiencePhases.includes(opt.id)}
                        onChange={() => togglePhase(opt.id)}
                      />
                    }
                    label={opt.label}
                  />
                ))}
              </FormGroup>
              <Typography
                variant="caption"
                sx={{ color: "rgba(0,0,0,0.6)", display: "block", mt: 0.5 }}
              >
                payments[].paymentPhase のいずれかが選択した期に一致するユーザーが対象になります。
              </Typography>
            </Box>
          )}

          <FormControlLabel
            value={PROMO_BANNER_AUDIENCE.ALL}
            control={<Radio />}
            label={
              <Typography sx={{ fontWeight: 700, color: textBase }}>
                ログイン中の全ユーザー
              </Typography>
            }
          />
        </RadioGroup>

        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <Button
            onClick={handleSaveAudience}
            disabled={
              savingAudience ||
              (audienceMode === PROMO_BANNER_AUDIENCE.PHASES &&
                audiencePhases.length === 0)
            }
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
            }}
          >
            {savingAudience ? "保存中…" : "表示対象を保存"}
          </Button>
        </Stack>
        {audienceMode === PROMO_BANNER_AUDIENCE.PHASES &&
          audiencePhases.length === 0 && (
            <Typography
              variant="caption"
              sx={{ color: "rgb(160,30,30)", display: "block", mt: 1 }}
            >
              対象とする期を1つ以上選んでください。
            </Typography>
          )}
      </Paper>

      {/* 文言・リンク編集 */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
        >
          文言・リンクの編集
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)", mb: 2 }}>
          タイトル・本文・リンクを編集します。本文の改行はそのまま表示されます。
          リンクは新しいタブで開きます。
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="アイコンのツールチップ文言"
            value={tooltipText}
            onChange={(e) => setTooltipText(e.target.value)}
            fullWidth
            size="small"
            helperText="左レール／モバイルフローティングのお知らせアイコンに表示されるツールチップ。例：周波数切り替えの大切なお知らせ"
          />
          <TextField
            label="チップ／カテゴリラベル"
            value={chipLabel}
            onChange={(e) => setChipLabel(e.target.value)}
            fullWidth
            size="small"
            helperText="ダイアログ上部の小さなラベル（例：お知らせ）。"
          />
          <TextField
            label="タイトル"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
            helperText="バナー・ダイアログ共通の太字見出し。"
          />
          <TextField
            label="本文（ダイアログのみ）"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={5}
            helperText="ダイアログに表示される本文。改行はそのまま表示されます。"
          />
          <TextField
            label="リンク先 URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            fullWidth
            size="small"
            placeholder="https://..."
            helperText="ボタンを押したときに新しいタブで開く URL。空欄ならボタンは出ません。"
          />
          <TextField
            label="リンクボタンの文言"
            value={linkLabel}
            onChange={(e) => setLinkLabel(e.target.value)}
            fullWidth
            size="small"
            helperText="例：詳しく見る／申し込みページへ"
          />
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
          <Button
            onClick={handleSaveText}
            disabled={savingText}
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
            }}
          >
            {savingText ? "保存中…" : "文言・リンクを保存"}
          </Button>
          <Button
            onClick={handleResetText}
            disabled={savingText}
            variant="outlined"
            startIcon={<RestartAltRoundedIcon />}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
          >
            既定値に戻す（未保存）
          </Button>
        </Stack>
      </Paper>

      {/* 画像 */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
        >
          画像
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)", mb: 2 }}>
          ダイアログ上部とバナー左の小アイコン位置に表示される画像。任意。
          アップロードすると Firebase Storage に保存され、保存と同時にバナーバージョンが上がります。
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt=""
              sx={{
                width: 120,
                height: 80,
                objectFit: "cover",
                borderRadius: 2,
                border: `1px solid ${border}`,
              }}
            />
          ) : (
            <Box
              sx={{
                width: 120,
                height: 80,
                borderRadius: 2,
                border: `1px dashed ${border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(0,0,0,0.4)",
                fontSize: 12,
              }}
            >
              （画像なし）
            </Box>
          )}

          <Stack direction="column" spacing={1}>
            <Button
              onClick={handleImagePick}
              disabled={uploadingImage}
              variant="contained"
              startIcon={<UploadRoundedIcon />}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: accent,
                "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
              }}
            >
              {uploadingImage ? "アップロード中…" : "画像を選んでアップロード"}
            </Button>
            {imageUrl ? (
              <Button
                onClick={handleRemoveImage}
                disabled={uploadingImage}
                variant="outlined"
                startIcon={<DeleteOutlineRoundedIcon />}
                color="error"
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                画像を削除
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageChange}
        />
      </Paper>

      {/* 表示終了日時 */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              background:
                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
              boxShadow:
                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <EventRoundedIcon sx={{ fontSize: 30, color: accent }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
            >
              表示終了日時
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
              この日時を過ぎるとバナー／ダイアログは自動で出なくなります。
              判定はサーバ時刻（JST）基準で行うため、ユーザーの端末時計の影響を受けません。
              未設定（空欄）にすると期限なしになります。
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
        >
          <TextField
            type="datetime-local"
            label="表示終了日時"
            value={expiresAt || ""}
            onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 1 }}
            size="small"
            sx={{ minWidth: 260 }}
            helperText="サーバ時刻（JST）基準で判定。"
          />
          {expiryInfo ? (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                color: expiryInfo.isPast ? "rgb(160,30,30)" : textBase,
              }}
            >
              プレビュー：{expiryInfo.label}
              {expiryInfo.isPast && "（期限を過ぎています）"}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.55)" }}>
              未設定（期限なし）
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <Button
            onClick={handleSaveExpiry}
            disabled={savingExpiry}
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
            }}
          >
            {savingExpiry ? "保存中…" : "期日を保存"}
          </Button>
          <Button
            onClick={handleClearExpiry}
            disabled={savingExpiry}
            variant="outlined"
            startIcon={<RestartAltRoundedIcon />}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
          >
            クリア（未保存）
          </Button>
        </Stack>
      </Paper>

      {/* プレビュー */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px dashed ${border}`,
          bgcolor: "rgba(0,0,0,0.02)",
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: textBase, mb: 1 }}
        >
          プレビュー（ダイアログ）
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 1.5 }}>
          現在編集中の文言で、実際のダイアログがどう見えるかを表示します（プレビューでは「今後表示しない」は保存されません）。
        </Typography>
        <PromoBannerDialog
          open
          chipLabel={chipLabel}
          title={title}
          body={body}
          imageUrl={imageUrl}
          linkUrl={linkUrl}
          linkLabel={linkLabel}
          onCloseToday={() =>
            showSnack("プレビューでは閉じる操作は保存されません。", "success")
          }
          onDismissForever={() =>
            showSnack("プレビューでは「今後表示しない」は保存されません。", "success")
          }
          previewMode
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={handleSnackbarClose}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
